/**
 * Demonstration data for the Parking category.
 * Kept separate from mock-data.ts so Sale/Rent/Lease demo data stays untouched.
 */

export type VehicleType = "car" | "bike" | "car_bike" | "ev" | "heavy";
export type ParkingType = "covered" | "open" | "basement" | "multi_level" | "street" | "valet";
export type ParkingDuration = "hourly" | "daily" | "monthly";

export interface ParkingListing {
  id: string;
  title: string;
  providerName: string;
  address: string;
  city: string;
  distanceKm: number;
  locationPrecision: "exact" | "approximate";
  parkingType: ParkingType;
  vehicleTypes: VehicleType[];
  totalSlots: number;
  availableSlots: number;
  priceHourly: number | null;
  priceDaily: number | null;
  priceMonthly: number | null;
  openingTime: string | null;
  closingTime: string | null;
  open24h: boolean;
  cctv: boolean;
  security: boolean;
  lighting: boolean;
  evCharging: boolean;
  verified: boolean;
  image: string;
  description: string;
  entryInstructions?: string;
  exitInstructions?: string;
  bookingEnabled: boolean;
  /** faux-map viewport position (%) */
  x: number;
  y: number;
  /** Real coordinates when available (live listings). */
  lat?: number;
  lng?: number;
  createdAt?: string;
}

export const PARKING_TYPES: { value: ParkingType; label: string }[] = [
  { value: "covered", label: "Covered" },
  { value: "open", label: "Open" },
  { value: "basement", label: "Basement" },
  { value: "multi_level", label: "Multi-level" },
  { value: "street", label: "Street side" },
  { value: "valet", label: "Valet" },
];

export const VEHICLE_TYPES: { value: VehicleType; label: string }[] = [
  { value: "car", label: "Car parking" },
  { value: "bike", label: "Bike parking" },
  { value: "car_bike", label: "Car & bike" },
  { value: "ev", label: "EV vehicles" },
  { value: "heavy", label: "Heavy vehicle" },
];

export const PARKING_DURATIONS: { value: ParkingDuration; label: string }[] = [
  { value: "hourly", label: "Hourly" },
  { value: "daily", label: "Daily" },
  { value: "monthly", label: "Monthly" },
];

const img = (seed: string) => `https://images.unsplash.com/${seed}?auto=format&fit=crop&w=800&q=70`;

export const MOCK_PARKING: ParkingListing[] = [
  {
    id: "pk1",
    title: "Green Park Covered Parking",
    providerName: "Green Park Facility Services",
    address: "Anna Nagar West",
    city: "Chennai",
    distanceKm: 0.8,
    locationPrecision: "exact",
    parkingType: "covered",
    vehicleTypes: ["car", "bike", "ev"],
    totalSlots: 60,
    availableSlots: 14,
    priceHourly: 40,
    priceDaily: 250,
    priceMonthly: 3500,
    openingTime: "06:00",
    closingTime: "23:00",
    open24h: false,
    cctv: true,
    security: true,
    lighting: true,
    evCharging: true,
    verified: true,
    image: img("photo-1590674899484-d5640e854abe"),
    description:
      "Covered multi-level parking with EV charging bays, 24x7 CCTV and on-site security staff.",
    entryInstructions: "Enter from 3rd Avenue gate, show booking ID at the boom barrier.",
    exitInstructions: "Exit via the rear ramp onto 5th Street.",
    bookingEnabled: true,
    x: 38,
    y: 36,
  },
  {
    id: "pk2",
    title: "Metro Station Open Parking",
    providerName: "City Metro Parking",
    address: "Guindy Metro",
    city: "Chennai",
    distanceKm: 2.1,
    locationPrecision: "approximate",
    parkingType: "open",
    vehicleTypes: ["car", "bike"],
    totalSlots: 120,
    availableSlots: 47,
    priceHourly: 20,
    priceDaily: 120,
    priceMonthly: 1800,
    openingTime: null,
    closingTime: null,
    open24h: true,
    cctv: true,
    security: false,
    lighting: true,
    evCharging: false,
    verified: true,
    image: img("photo-1506521781263-d8422e82f27a"),
    description: "Open commuter parking next to the metro entrance. 24-hour access with CCTV.",
    bookingEnabled: false,
    x: 60,
    y: 54,
  },
  {
    id: "pk3",
    title: "Apartment Basement Parking Slot",
    providerName: "Sunrise Residency",
    address: "T. Nagar",
    city: "Chennai",
    distanceKm: 3.4,
    locationPrecision: "approximate",
    parkingType: "basement",
    vehicleTypes: ["car"],
    totalSlots: 4,
    availableSlots: 1,
    priceHourly: null,
    priceDaily: 200,
    priceMonthly: 2800,
    openingTime: "05:30",
    closingTime: "22:30",
    open24h: false,
    cctv: true,
    security: true,
    lighting: true,
    evCharging: false,
    verified: false,
    image: img("photo-1573348722427-f1d6819fdf98"),
    description: "Private basement slot inside a gated residential tower. Monthly rentals only.",
    bookingEnabled: false,
    x: 26,
    y: 62,
  },
  {
    id: "pk4",
    title: "Tech Park Office Bike Parking",
    providerName: "OMR Office Parking Co.",
    address: "OMR, Perungudi",
    city: "Chennai",
    distanceKm: 6.2,
    locationPrecision: "exact",
    parkingType: "multi_level",
    vehicleTypes: ["bike", "car_bike"],
    totalSlots: 300,
    availableSlots: 88,
    priceHourly: 10,
    priceDaily: 60,
    priceMonthly: 900,
    openingTime: null,
    closingTime: null,
    open24h: true,
    cctv: true,
    security: true,
    lighting: true,
    evCharging: true,
    verified: true,
    image: img("photo-1470224114660-3f6686c562eb"),
    description:
      "Large multi-level facility serving the IT corridor. Separate bike decks and EV points.",
    bookingEnabled: true,
    x: 74,
    y: 30,
  },
];

/** Parking-only filter option catalogue used by the search/filter panel. */
export const PARKING_AMENITY_FILTERS = [
  { value: "covered", label: "Covered parking" },
  { value: "open", label: "Open parking" },
  { value: "ev_charging", label: "EV charging" },
  { value: "cctv", label: "CCTV" },
  { value: "security", label: "Security" },
  { value: "open_24h", label: "24-hour access" },
  { value: "available_now", label: "Available now" },
  { value: "verified", label: "Verified only" },
] as const;

export type ParkingAmenityFilter = (typeof PARKING_AMENITY_FILTERS)[number]["value"];

/** Placeholder booking record shape for the future parking booking module. */
export interface ParkingBookingDraft {
  parkingId: string;
  vehicleType: VehicleType;
  duration: ParkingDuration;
  startsAt: string | null;
  endsAt: string | null;
  notes: string;
}
